import React, { useId } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { FormControlLabel } from "../FormControlWrapper/FormControlLabel";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import { cn } from "@gettactic/helpers";
import {
  CheckboxInputProps,
  CheckboxInputPropsWithFormControl,
  CheckboxPosition
} from "./CheckboxInput.types";

const checkboxHoverClassBase = "outline outline-[3px] outline-neutral-50";
const checkboxHoverClass = checkboxHoverClassBase
  .split(" ")
  .map((c) => `hover:${c} group-hover:${c}`)
  .join(" ");

const CheckboxInputInput = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxInputProps
>((props, ref) => {
  const {
    checked,
    onCheckedChange,
    disableOutline,
    checkboxPosition,
    className,
    ...rest
  } = props;

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      checked={checked}
      onCheckedChange={(checkedState) => {
        // Convert CheckedState to boolean
        onCheckedChange?.(checkedState === true);
      }}
      className={cn(
        "peer h-4 w-4 shrink-0 rounded-md",
        "border border-neutral-400",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:border-none data-[state=checked]:bg-black data-[state=checked]:text-white",
        checkboxHoverClass,
        className
      )}
      {...rest}
    >
      <CheckboxPrimitive.Indicator
        className={cn("flex items-center justify-center text-current")}
      >
        <CheckIcon className="h-4 w-4" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

CheckboxInputInput.displayName = "CheckboxInputInput";

export const CheckboxInput = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxInputPropsWithFormControl
>((props, ref) => {
  const { checkboxPosition = "bottom", ...restProps } = props;
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(restProps);
  const {
    label,
    description,
    info,
    isRequired,
    isOptional,
    error,
    ...restWrapperProps
  } = wrapperProps;
  const { checked, onCheckedChange, disabled } = inputProps;

  const handleLabelClick = () => {
    if (!disabled && onCheckedChange && checked !== undefined) {
      onCheckedChange(!checked);
    }
  };

  // For left/right positions, we need a custom layout
  if (checkboxPosition === "left" || checkboxPosition === "right") {
    // The box itself carries the invalid state: on its own the message alone
    // reads as body copy and gets lost next to a long acknowledgement.
    const invalidBoxClass =
      error && "border-red-700 data-[state=checked]:bg-red-700";

    return (
      <div className="w-full">
        <div className="flex items-start justify-between gap-4">
          {checkboxPosition === "left" && (
            <div className="flex shrink-0 items-center pt-0.5">
              <CheckboxInputInput
                ref={ref}
                {...inputProps}
                className={invalidBoxClass || undefined}
                checkboxPosition={checkboxPosition}
              />
            </div>
          )}
          <div
            className={cn(
              "flex min-w-0 flex-1 flex-col",
              !disabled && onCheckedChange && "cursor-pointer"
            )}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest(
                  '[role="tooltip"], [data-radix-tooltip-content], [data-radix-tooltip-trigger], button'
                )
              ) {
                return;
              }
              handleLabelClick();
            }}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={(e) => {
              if (!disabled && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleLabelClick();
              }
            }}
          >
            {label && (
              <FormControlLabel
                isRequired={isRequired}
                isOptional={isOptional}
                info={info}
              >
                {label}
              </FormControlLabel>
            )}
            {description && (
              <p className="text-md mt-1 text-justify text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {checkboxPosition === "right" && (
            <div className="flex w-12 shrink-0 items-center justify-end pt-0.5">
              <CheckboxInputInput
                ref={ref}
                {...inputProps}
                className={invalidBoxClass || undefined}
                checkboxPosition={checkboxPosition}
              />
            </div>
          )}
        </div>
        {/* The left/right layout bypasses FormControlWrapper, so the validation
            message has to be rendered here or it would be swallowed. */}
        {error && (
          <p className="mt-2 font-medium text-red-700 text-sm">{error}</p>
        )}
        {inputProps.disableOutline !== true && <FormControlOutline />}
      </div>
    );
  }

  // Default bottom position - use FormControlWrapper
  const checkboxId = useId();

  const handleLabelAreaClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest(
        '[role="tooltip"], [data-radix-tooltip-content], [data-radix-tooltip-trigger], button'
      )
    ) {
      return;
    }
    handleLabelClick();
  };

  const handleLabelAreaKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleLabelClick();
    }
  };

  return (
    <FormControlWrapper
      variant={"unstyled"}
      {...restWrapperProps}
      label={
        label ? (
          <div
            className={cn(
              "w-full",
              !disabled && onCheckedChange && "cursor-pointer"
            )}
            onClick={handleLabelAreaClick}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={handleLabelAreaKeyDown}
          >
            {label}
          </div>
        ) : undefined
      }
      description={
        description ? (
          <div
            className={cn(!disabled && onCheckedChange && "cursor-pointer")}
            onClick={handleLabelAreaClick}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={handleLabelAreaKeyDown}
          >
            {description}
          </div>
        ) : undefined
      }
      info={info}
      isRequired={isRequired}
      isOptional={isOptional}
      error={error}
    >
      <CheckboxInputInput
        ref={ref}
        {...inputProps}
        checkboxPosition={checkboxPosition}
        id={checkboxId}
      />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

CheckboxInput.displayName = "CheckboxInput";
